import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, IndianRupee, Pencil, Trash2 } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const inputCls = "bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900";

export default function Ledgers() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "client");
  const [partyId, setPartyId] = useState(params.get("id") || "");
  const [parties, setParties] = useState([]);
  const [banks, setBanks] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [open, setOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", account_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [editEntry, setEditEntry] = useState(null); // {entry_id, description, date, amount, account_id}

  useEffect(() => {
    api.get(type === "client" ? "/clients" : "/vendors").then((r) => {
      setParties(r.data);
      if (!partyId && r.data.length) setPartyId(r.data[0][type === "client" ? "client_id" : "vendor_id"]);
    }).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  }, [type]);

  const reload = () => {
    if (!partyId) return setLedger(null);
    api.get(`/ledger/${type}/${partyId}`).then((r) => setLedger(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, [type, partyId]);

  const party = parties.find((p) => (type === "client" ? p.client_id : p.vendor_id) === partyId);

  const recordPayment = async (e) => {
    e.preventDefault();
    if (!pay.account_id || Number(pay.amount) <= 0) return toast.error("Enter amount and account");
    setBusy(true);
    try {
      await api.post("/ledger/payment", {
        party_type: type, party_id: partyId,
        amount: Number(pay.amount), account_id: pay.account_id, note: pay.note,
      });
      toast.success("Payment recorded — ledger & bank updated");
      setOpen(false); setPay({ amount: "", account_id: "", note: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  const openEdit = (en) => setEditEntry({
    entry_id: en.entry_id, description: en.description, date: en.date || "",
    amount: en.debit > 0 ? en.debit : en.credit, side: en.debit > 0 ? "debit" : "credit",
    account_id: en.account_id || "", linked: Boolean(en.ref_id),
  });

  const saveEdit = async (e) => {
    e.preventDefault();
    if (Number(editEntry.amount) <= 0) return toast.error("Amount must be positive");
    setBusy(true);
    try {
      await api.put(`/ledger/${editEntry.entry_id}`, {
        description: editEntry.description,
        date: editEntry.date || undefined,
        debit: editEntry.side === "debit" ? Number(editEntry.amount) : 0,
        credit: editEntry.side === "credit" ? Number(editEntry.amount) : 0,
        account_id: editEntry.account_id,
      });
      toast.success("Ledger entry updated");
      setEditEntry(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const removeEntry = async (en) => {
    const warn = en.ref_id
      ? `Delete "${en.description}"? It is linked to a document (invoice/payment/purchase) — the document itself will NOT change. This cannot be undone.`
      : `Delete "${en.description}"? This cannot be undone.`;
    if (!window.confirm(warn)) return;
    try {
      await api.delete(`/ledger/${en.entry_id}`);
      toast.success("Ledger entry deleted");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="portal-party-ledgers">
      <PageHeader title="Party Ledgers" sub="Opening dues + debit / credit history with running balance for every client and vendor" />

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex rounded-full border border-slate-300 overflow-hidden" data-testid="ledger-type-toggle">
          {["client", "vendor"].map((t) => (
            <button key={t} onClick={() => { setType(t); setPartyId(""); setParams({}); }} data-testid={`ledger-type-${t}`}
              className={`px-5 py-2 text-sm font-semibold capitalize transition-colors duration-200 ${type === t ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
              {t}s
            </button>
          ))}
        </div>
        <select value={partyId} onChange={(e) => setPartyId(e.target.value)} data-testid="ledger-party-select" className={`${selCls} max-w-xs`}>
          <option value="">Select {type}…</option>
          {parties.map((p) => (
            <option key={type === "client" ? p.client_id : p.vendor_id} value={type === "client" ? p.client_id : p.vendor_id}>
              {p.name}
            </option>
          ))}
        </select>
        {party && (
          <Button onClick={() => setOpen(true)} data-testid="record-payment-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <IndianRupee className="w-4 h-4 mr-1" /> Record Payment
          </Button>
        )}
        {type === "client" && party && ledger && ledger.balance > 0 && (
          <button
            data-testid="send-reminder-btn"
            onClick={async () => {
              if (!party?.email) return toast.error("This client has no email on record");
              try {
                const r = await api.post("/ledger/remind", { party_id: partyId });
                toast.success(`Reminder emailed to ${r.data.email} for ${inr(r.data.balance)}`);
              } catch (err) {
                const d = err.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Failed to send reminder");
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 text-amber-600 hover:bg-amber-500 hover:text-slate-950 text-sm font-semibold px-4 py-2 transition-colors duration-200">
            Email Payment Reminder
          </button>
        )}
      </div>

      {!partyId || !ledger ? (
        <EmptyState icon={BookOpen} title="Select a party" sub="Choose a client or vendor to view their ledger" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="ledger-card">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-white/90">
            <div>
              <p className="font-semibold text-slate-900" data-testid="ledger-party-name">{party?.name}</p>
              {party?.gstin && <p className="text-xs font-mono2 text-emerald-600">GSTIN {party.gstin}</p>}
              {Number(party?.credit_days) > 0 && (
                <p className="text-xs text-amber-600" data-testid="ledger-credit-terms">Credit terms: {party.credit_days} days</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[11px] font-mono2 uppercase tracking-wider text-slate-500">
                {type === "client" ? "Receivable balance" : "Payable balance"}
              </p>
              <p className={`font-mono2 font-bold text-xl ${ledger.balance > 0 ? (type === "client" ? "text-amber-600" : "text-red-600") : "text-emerald-600"}`} data-testid="ledger-balance">
                {inr(Math.abs(ledger.balance))}{ledger.balance > 0 ? (type === "client" ? " Dr" : " Cr") : ""}
              </p>
            </div>
          </div>
          {ledger.entries.length === 0 ? (
            <EmptyState icon={BookOpen} title="No transactions yet" sub="Invoices, purchases and payments will appear here" />
          ) : (
            <table className="w-full text-sm min-w-[760px]" data-testid="ledger-table">
              <thead>
                <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">Date</th><th className="px-5 py-3">Particulars</th>
                  <th className="px-5 py-3 text-right">Debit (Dr)</th><th className="px-5 py-3 text-right">Credit (Cr)</th>
                  <th className="px-5 py-3 text-right">Balance</th>
                  {user?.role === "admin" && <th className="px-5 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {ledger.entries.map((e) => (
                  <tr key={e.entry_id} className={`transition-colors duration-150 ${e.opening ? "bg-emerald-50/60" : "hover:bg-slate-50"}`} data-testid={`ledger-entry-${e.entry_id}`}>
                    <td className="px-5 py-3 text-slate-600">{e.date ? fdate(e.date) : "—"}</td>
                    <td className="px-5 py-3 text-slate-800">
                      {e.opening
                        ? <span className="inline-flex items-center gap-2"><span className="px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-700 bg-emerald-500/10 text-[10px] font-mono2 uppercase">Opening</span>{e.description}</span>
                        : e.description}
                    </td>
                    <td className="px-5 py-3 text-right font-mono2 text-red-600">{e.debit ? inr(e.debit) : "—"}</td>
                    <td className="px-5 py-3 text-right font-mono2 text-emerald-600">{e.credit ? inr(e.credit) : "—"}</td>
                    <td className="px-5 py-3 text-right font-mono2 font-semibold text-slate-900">{inr(Math.abs(e.balance))}</td>
                    {user?.role === "admin" && (
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {!e.opening && (
                          <>
                            <button onClick={() => openEdit(e)} data-testid={`ledger-edit-${e.entry_id}`}
                              className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => removeEntry(e)} data-testid={`ledger-delete-${e.entry_id}`}
                              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle>{type === "client" ? "Receive Payment from" : "Make Payment to"} {party?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={recordPayment} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
              <Input type="number" step="0.01" min="0" required value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" className={`${inputCls} font-mono2`} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{type === "client" ? "Deposit into" : "Pay from"} *</Label>
              <select value={pay.account_id} onChange={(e) => setPay({ ...pay, account_id: e.target.value })} data-testid="payment-account-select" className={selCls}>
                <option value="">Select account…</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name} — {inr(b.balance)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Note</Label>
              <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="payment-note-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={busy} data-testid="payment-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Recording…" : "Record Payment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editEntry)} onOpenChange={(v) => { if (!v) setEditEntry(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="ledger-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Ledger Entry {editEntry?.side === "debit" ? "(Debit)" : "(Credit)"}</DialogTitle>
          </DialogHeader>
          {editEntry?.linked && (
            <p className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 mt-2">
              Linked to a document — the invoice/payment itself will not change.
            </p>
          )}
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Particulars *</Label>
              <Input required value={editEntry?.description || ""} onChange={(e) => setEditEntry({ ...editEntry, description: e.target.value })} data-testid="ledger-edit-description" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
                <Input type="number" step="0.01" min="0" required value={editEntry?.amount ?? ""} onChange={(e) => setEditEntry({ ...editEntry, amount: e.target.value })} data-testid="ledger-edit-amount" className={`${inputCls} font-mono2`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Date</Label>
                <Input type="date" value={editEntry?.date || ""} onChange={(e) => setEditEntry({ ...editEntry, date: e.target.value })} data-testid="ledger-edit-date" className={inputCls} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Bank / Cash Account</Label>
              <select value={editEntry?.account_id || ""} onChange={(e) => setEditEntry({ ...editEntry, account_id: e.target.value })} data-testid="ledger-edit-account" className={selCls}>
                <option value="">None (off-book)</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name}</option>)}
              </select>
            </div>
            <Button type="submit" disabled={busy} data-testid="ledger-edit-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : "Save Entry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
